import { ActionsHeaderCell } from '@/components/ActionsHeaderCell';
import { MoneyText } from '@/components/MoneyText';
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  List,
  ListItem,
  MenuItem,
  Popover,
  Select,
  Skeleton,
  Stack,
  TableCell,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { visuallyHidden } from '@mui/utils';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { errorCopy } from '@/api/errorCopy';
import { ErrorCodes, isApiError } from '@/api/errors';
import {
  payeesApi,
  type Payee,
  type PayeeKind,
  type PayeeOrderRow,
  type PayeeStatus,
} from '@/api/payees';
import { ClearFiltersButton } from '@/components/ClearFiltersButton';
import { formatCivilDate } from '@/i18n/format';
import { useLocale } from '@/i18n/useLocale';
import { useMe } from '@/session/meHooks';
import { TabularText } from '@/components/TabularText';
import { ReorderHandle, type ReorderHandleProps } from '@/components/SortableCardGrid';
import { OrderHandleCell, SortableTable } from '@/components/SortableTable';
import { TableCard } from '@/components/TableCard';
import { OverflowMenu } from '@/shell/OverflowMenu';
import { V2_TABLE_HEADS_SX } from '@/components/v2TableHeads';
import { PageHeader } from '@/shell/PageHeader';
import { usePageTitle } from '@/shell/usePageTitle';
import { useNotify } from '@/shell/NotifyProvider';
import { useDebouncedText } from '@/shared/useDebouncedText';
import {
  activeCount,
  defaultsOf,
  enumParam,
  optionalEnumParam,
  stringParam,
  useUrlState,
} from '@/shared/useUrlState';
import { ReviewPanel, ReviewTabLoading, ReviewTabs } from '@/components/ReviewTabs';
import { useReviewCount, useReviewTab, type ReviewCount } from '@/components/reviewTab';
import { SubscriberActionHint, useSubscriberAction } from '@/session/useSubscriberAction';
import { announceReviewChanged } from '@/shell/reviewChanged';
import { NamingStatusCard } from './NamingStatusCard';
import { PayeeProposalList } from './PayeeProposalList';
import { UnnamedPayeesSection } from './UnnamedPayeesSection';
import { ErrorState } from '@/components/ErrorState';
import { SubmitButton } from '@/components/SubmitButton';
import { GuardedDialog } from '@/components/GuardedDialog';
import { useDialogDirtyGuard } from '@/shared/useDialogDirtyGuard';
import { useDirty } from '@/shared/useDirty';
import { ALIAS_KIND_KEYS, kindOf } from './aliasKind';

type ListState =
  { status: 'loading' } | { status: 'ready'; rows: Payee[] } | { status: 'error'; error: unknown };

interface DirectoryRow {
  id: number;
  name: string;
  payee: Payee | null;
  order: PayeeOrderRow | null;
}

type PayeesTab = 'review' | 'payees';
const URL_SCHEMA = {
  q: stringParam(),
  status: enumParam<PayeeStatus>(['ACTIVE', 'ARCHIVED'], 'ACTIVE'),
  // Absent: the default by the Items to review count (TMMAPP-0159 §F 27); a chosen tab is kept.
  tab: optionalEnumParam<PayeesTab>(['review', 'payees']),
};
const FILTERS = ['q', 'status'] as const;
const REVIEW_PANEL_ID = 'payees-review';

/** Order rows first, then active payees the order omits, in the list's own order. */
function directoryRows(payees: Payee[], order: PayeeOrderRow[] | null): DirectoryRow[] {
  if (order === null) {
    return payees.map((payee) => ({ id: payee.id, name: payee.name, payee, order: null }));
  }
  const byId = new Map(payees.map((payee) => [payee.id, payee]));
  const ordered = new Set(order.map((row) => row.payeeId));
  const head = order.map((row) => {
    const payee = byId.get(row.payeeId) ?? null;
    return { id: row.payeeId, name: payee?.name ?? row.payeeName, payee, order: row };
  });
  const tail = payees
    .filter((payee) => !ordered.has(payee.id))
    .map((payee) => ({ id: payee.id, name: payee.name, payee, order: null }));
  return [...head, ...tail];
}

/**
 * Counterparty directory `/me/payees` (TMMAPP-0029 + naming review TMMAPP-0032). Two tabs for a
 * subscriber (TMMAPP-0159 §F 31): **Items to review** (the naming coverage and the names to review,
 * with Forgotten suggestions) and **Payees** (the table, its search and filters). With no search
 * and Status Active, the table is the matching order of payees (TMMAPP-0130): drag, keyboard or
 * Move earlier / Move later. An old `?tab=matching-order` link lands on the Payees tab.
 *
 * TMMAPP-0164 item 12: below "Names to review", **Left for you to name** (`UnnamedPayeesSection`).
 * It isn't an item to review, so it adds nothing to the tab's count; the coverage card's **Name
 * them** brings it into view and focuses its heading.
 */
export function PayeesPage() {
  const { t } = useTranslation();
  const locale = useLocale();
  const me = useMe();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [state, setState] = useState<ListState>({ status: 'loading' });
  // The search and the status are in the URL; the field types into local text that reaches the URL
  // (replace, never a history entry per keystroke) once the person pauses.
  const [urlState, setUrl] = useUrlState(URL_SCHEMA);
  const { q, status, tab: requestedTab } = urlState;
  const [searchText, setSearchText] = useDebouncedText(q, (value) => setUrl({ q: value }));
  const [createOpen, setCreateOpen] = useState(false);
  const [name, setName] = useState('');
  const [kind, setKind] = useState<PayeeKind>('VENDOR');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [namingReload, setNamingReload] = useState(0);
  const [proposalsReload, setProposalsReload] = useState(0);
  // Left for you to name (item 12): re-read when names were decided elsewhere on the tab, never
  // after its own writes (they remove their row in place). The kept count is the card's served one.
  const [unnamedReload, setUnnamedReload] = useState(0);
  const [keptUnnamed, setKeptUnnamed] = useState<number | null>(null);
  const [unnamedFocus, setUnnamedFocus] = useState(0);
  const notify = useNotify();
  const namingDebounce = useRef<number | null>(null);
  const [order, setOrder] = useState<PayeeOrderRow[] | null>(null);
  const [orderError, setOrderError] = useState<unknown>(null);
  const [reresolve, setReresolve] = useState<number | null>(null);
  const [reresolving, setReresolving] = useState(false);
  const [patternMenu, setPatternMenu] = useState<{ id: number; anchor: HTMLElement } | null>(null);
  const reorderTicket = useRef(0);
  // The order the next move reads. Updated in the handler, before paint, so a second move sends
  // the list after the first.
  const orderRef = useRef<PayeeOrderRow[]>([]);
  const title = usePageTitle({ title: t('toolsNav.payees') });
  // The new-payee form empties on every close, so dirty is "differs from the empty form".
  const { dirty: createDirty } = useDirty({ name, kind, notes });
  const closeCreate = () => {
    setCreateOpen(false);
    setName('');
    setKind('VENDOR');
    setNotes('');
  };
  const createGuard = useDialogDirtyGuard(createOpen && createDirty, closeCreate, busy);

  const guest = me.tier === 'GUEST';
  const readOnly = me.tier !== 'SUBSCRIBER';

  const createPayee = useSubscriberAction(() => setCreateOpen(true));
  const currency = me.user.currency;
  const ordering = q.trim().length === 0 && status === 'ACTIVE';

  // An old matching-order link opens the Payees tab; any other unknown `tab` is dropped (replace).
  useLayoutEffect(() => {
    const raw = searchParams.get('tab');
    if (raw === null || raw === 'review' || raw === 'payees') return;
    const next = new URLSearchParams(searchParams);
    if (raw === 'matching-order') next.set('tab', 'payees');
    else next.delete('tab');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  // Items to review (a subscriber's naming work): its count is the names to review as served, plus
  // the coverage card while it still offers naming work, so "All caught up" never sits above an
  // offer to name more (TMMAPP-0160 item 3).
  const [namesCount, reportNamesCount] = useReviewCount();
  const [coverageOpen, setCoverageOpen] = useState<boolean | null>(null);
  const reviewCount: ReviewCount =
    coverageOpen === null || namesCount === 'loading'
      ? 'loading'
      : typeof namesCount === 'number'
        ? namesCount + (coverageOpen ? 1 : 0)
        : coverageOpen
          ? 1
          : namesCount;
  // A link with the table's search or status is a link to the Payees tab, like a `?tab=`.
  const tableLink = q !== '' || status !== 'ACTIVE';
  const tab = useReviewTab<'payees'>(
    requestedTab ?? (tableLink ? 'payees' : null),
    reviewCount,
    'payees',
    !readOnly,
  );
  const showTab = (next: PayeesTab) => setUrl({ tab: next }, { push: true });

  const load = useCallback(async () => {
    setState({ status: 'loading' });
    try {
      const listQuery = {
        status,
        ...(q.trim().length > 0 ? { q: q.trim() } : {}),
        withTotals: true,
      };
      if (q.trim().length === 0 && status === 'ACTIVE') {
        const [listResult, orderResult] = await Promise.allSettled([
          payeesApi.list(listQuery),
          payeesApi.order(),
        ]);
        if (listResult.status === 'rejected') throw listResult.reason;
        if (orderResult.status === 'fulfilled') {
          orderRef.current = orderResult.value;
          setOrder(orderResult.value);
          setOrderError(null);
        } else {
          orderRef.current = [];
          setOrder(null);
          setOrderError(orderResult.reason);
        }
        setState({ status: 'ready', rows: listResult.value });
      } else {
        const rows = await payeesApi.list(listQuery);
        setOrder(null);
        setOrderError(null);
        setState({ status: 'ready', rows });
      }
    } catch (error) {
      setState({ status: 'error', error });
    }
  }, [q, status]);

  useEffect(() => {
    if (guest) return;
    queueMicrotask(() => {
      void load();
    });
  }, [guest, load]);

  async function reorder(ids: Array<string | number>) {
    const before = orderRef.current;
    if (before.length === 0) return;
    const byId = new Map(before.map((row) => [row.payeeId, row]));
    const next = ids.flatMap((id) => {
      const row = byId.get(Number(id));
      return row ? [row] : [];
    });
    if (next.length !== before.length) return;
    const ticket = ++reorderTicket.current;
    orderRef.current = next;
    setOrder(next);
    setReresolve(null);
    try {
      const saved = await payeesApi.reorder(next.map((row) => row.payeeId));
      if (ticket !== reorderTicket.current) return;
      orderRef.current = saved;
      setOrder(saved);
      const count = await payeesApi.reresolve(true).catch(() => 0);
      if (ticket === reorderTicket.current && count > 0) setReresolve(count);
    } catch (error) {
      if (ticket !== reorderTicket.current) throw error;
      orderRef.current = before;
      setOrder(before);
      if (isApiError(error) && error.code === ErrorCodes.REORDER_SET_MISMATCH) {
        notify(t('rules.reorderMismatch'), { severity: 'error' });
        await load();
      } else {
        notify(errorCopy(t, error, 'matchingOrder.reorderFailed'), { severity: 'error' });
      }
      throw error;
    }
  }

  async function runReresolve() {
    if (reresolving) return;
    setReresolving(true);
    try {
      const moved = await payeesApi.reresolve(false);
      setReresolve(null);
      notify(t('matchingOrder.reresolved', { count: moved }));
    } catch (error) {
      notify(errorCopy(t, error, 'matchingOrder.reresolveFailed'), { severity: 'error' });
    } finally {
      setReresolving(false);
    }
  }

  function scheduleNamingRefetch() {
    if (namingDebounce.current !== null) window.clearTimeout(namingDebounce.current);
    namingDebounce.current = window.setTimeout(() => {
      setNamingReload((n) => n + 1);
      namingDebounce.current = null;
    }, 500);
  }

  async function handleCreate() {
    if (name.trim().length === 0) return;
    setBusy(true);
    try {
      const created = await payeesApi.create({
        name: name.trim(),
        kind,
        ...(notes.trim().length > 0 ? { notes: notes.trim() } : {}),
      });
      setCreateOpen(false);
      setName('');
      setNotes('');
      navigate(`/me/payees/${created.id}`);
    } catch {
      setBusy(false);
    } finally {
      setBusy(false);
    }
  }

  async function toggleExcluded(payee: Payee) {
    try {
      await payeesApi.patch(payee.id, { aiExcluded: !payee.aiExcluded });
      await load();
    } catch {
      /* leave list as-is */
    }
  }

  const directory =
    state.status === 'ready' ? directoryRows(state.rows, ordering ? order : null) : [];

  if (guest) {
    return (
      <>
        <PageHeader
          title={title}
          subtitle={t('payee.subtitle')}
          actions={
            <>
              <Button
                variant="contained"
                aria-describedby={createPayee.descriptionId}
                onClick={createPayee.run}
              >
                {t('payee.new')}
              </Button>
              <SubscriberActionHint
                id={createPayee.descriptionId}
                description={createPayee.description}
              />
            </>
          }
        />
        <Typography variant="body1">{t('payee.empty')}</Typography>
      </>
    );
  }

  return (
    // The v2 table heads (TMMAPP-0151 §H 24), scoped to this page.
    <Box sx={V2_TABLE_HEADS_SX} data-v2-table-heads="">
      <PageHeader
        title={title}
        subtitle={t('payee.subtitle')}
        actions={
          <>
            <Button
              variant="contained"
              aria-describedby={createPayee.descriptionId}
              onClick={createPayee.run}
            >
              {t('payee.new')}
            </Button>
            <SubscriberActionHint
              id={createPayee.descriptionId}
              description={createPayee.description}
            />
          </>
        }
      />
      {!readOnly && (
        <ReviewTabs
          value={tab}
          count={reviewCount}
          tabs={[{ value: 'payees', label: t('nav.payees') }]}
          onChange={showTab}
          label={t('payee.listTabsLabel')}
          panelId={REVIEW_PANEL_ID}
          sx={{ mb: 2 }}
        />
      )}
      {tab === null && <ReviewTabLoading />}
      {!readOnly && (
        <ReviewPanel
          id={REVIEW_PANEL_ID}
          active={tab === 'review'}
          count={reviewCount}
          mainLabel={t('nav.payees')}
          onMain={() => showTab('payees')}
        >
          <NamingStatusCard
            reloadToken={namingReload}
            onNotify={notify}
            onOpenWork={setCoverageOpen}
            onKeptCount={setKeptUnnamed}
            onShowUnnamed={() => {
              if (tab !== 'review') showTab('review');
              setUnnamedFocus((n) => n + 1);
            }}
            onRan={() => {
              // A run's suggestions grew or it ended: the list re-reads in place, and the
              // sidebar's count re-reads its summary (TMMAPP-0163 item 3).
              setProposalsReload((n) => n + 1);
              setUnnamedReload((n) => n + 1);
              announceReviewChanged();
            }}
          />
          <PayeeProposalList
            reloadToken={proposalsReload}
            onNotify={notify}
            onDecided={() => {
              scheduleNamingRefetch();
              setUnnamedReload((n) => n + 1);
            }}
            onCount={reportNamesCount}
            caughtUpShown
          />
          <UnnamedPayeesSection
            reloadToken={unnamedReload}
            keptCount={keptUnnamed}
            readOnly={readOnly}
            focusToken={unnamedFocus}
            onChanged={scheduleNamingRefetch}
            onNotify={notify}
          />
        </ReviewPanel>
      )}
      {tab === 'payees' && (
        <Stack spacing={2}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            sx={{ gap: 1.5, alignItems: { md: 'center' }, justifyContent: 'space-between' }}
          >
            <Stack direction={{ xs: 'column', sm: 'row' }} sx={{ gap: 1.5, flex: 1 }}>
              <TextField
                label={t('payee.search')}
                value={searchText}
                onChange={(event) => setSearchText(event.target.value)}
                size="small"
                fullWidth
              />
              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel id="payee-status-label">{t('payee.statusLabel')}</InputLabel>
                <Select
                  labelId="payee-status-label"
                  label={t('payee.statusLabel')}
                  value={status}
                  onChange={(event) => setUrl({ status: event.target.value as PayeeStatus })}
                >
                  <MenuItem value="ACTIVE">{t('payee.statusActive')}</MenuItem>
                  <MenuItem value="ARCHIVED">{t('payee.statusArchived')}</MenuItem>
                </Select>
              </FormControl>
              <ClearFiltersButton
                active={activeCount(URL_SCHEMA, urlState, FILTERS)}
                onClear={() => setUrl(defaultsOf(URL_SCHEMA, FILTERS))}
              />
            </Stack>
          </Stack>

          {!ordering && (
            <Typography
              variant="caption"
              component="p"
              sx={{ color: 'text.secondary' }}
              data-order-locked=""
            >
              {t('payee.orderLocked')}
            </Typography>
          )}
          {state.status === 'loading' && <Skeleton variant="rounded" height={120} />}
          {state.status === 'error' && (
            <ErrorState
              variant="section"
              error={state.error}
              what={t('errors.what.payees')}
              onRetry={() => void load()}
            />
          )}
          {state.status === 'ready' && directory.length === 0 && (
            <Typography>{t('payee.empty')}</Typography>
          )}
          {state.status === 'ready' && directory.length > 0 && (
            <PayeeTable
              rows={directory}
              ordering={ordering}
              readOnly={readOnly}
              currency={currency}
              locale={locale}
              reresolve={reresolve}
              reresolving={reresolving}
              onReorder={reorder}
              onReresolve={() => void runReresolve()}
              onDismissReresolve={() => setReresolve(null)}
              onOpenPatterns={(id, anchor) => setPatternMenu({ id, anchor })}
              onToggleExcluded={(payee) => void toggleExcluded(payee)}
              onOpen={(id) => navigate(`/me/payees/${id}`)}
              orderError={orderError}
            />
          )}
        </Stack>
      )}
      <PatternPopover menu={patternMenu} order={order} onClose={() => setPatternMenu(null)} />

      <GuardedDialog guard={createGuard} open={createOpen} fullWidth maxWidth="sm">
        <DialogTitle>{t('payee.new')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label={t('payee.name')}
              value={name}
              onChange={(event) => setName(event.target.value)}
              required
              fullWidth
            />
            <FormControl fullWidth>
              <InputLabel id="new-kind">{t('payee.kindLabel')}</InputLabel>
              <Select
                labelId="new-kind"
                label={t('payee.kindLabel')}
                value={kind}
                onChange={(event) => setKind(event.target.value as PayeeKind)}
              >
                <MenuItem value="VENDOR">{t('payee.kind.VENDOR')}</MenuItem>
                <MenuItem value="PERSON">{t('payee.kind.PERSON')}</MenuItem>
                <MenuItem value="OTHER">{t('payee.kind.OTHER')}</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label={t('payee.notes')}
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
              fullWidth
              multiline
              minRows={2}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={createGuard.requestClose}>{t('common.cancel')}</Button>
          <SubmitButton
            onClick={() => void handleCreate()}
            disabled={name.trim().length === 0 || busy}
            loading={busy}
          >
            {t('common.save')}
          </SubmitButton>
        </DialogActions>
      </GuardedDialog>
    </Box>
  );
}

function PayeeTable({
  rows,
  ordering,
  readOnly,
  currency,
  locale,
  reresolve,
  reresolving,
  onReorder,
  onReresolve,
  onDismissReresolve,
  onOpenPatterns,
  onToggleExcluded,
  onOpen,
  orderError,
}: {
  rows: DirectoryRow[];
  ordering: boolean;
  readOnly: boolean;
  currency: string | null;
  locale: string;
  reresolve: number | null;
  reresolving: boolean;
  onReorder: (ids: Array<string | number>) => void | Promise<void>;
  onReresolve: () => void;
  onDismissReresolve: () => void;
  onOpenPatterns: (id: number, anchor: HTMLElement) => void;
  onToggleExcluded: (payee: Payee) => void;
  onOpen: (id: number) => void;
  orderError: unknown;
}) {
  const { t } = useTranslation();
  const theme = useTheme();
  const md = useMediaQuery(theme.breakpoints.up('md'));
  const showHandles = ordering && !readOnly && rows.some((row) => row.order !== null);
  const menu = (row: DirectoryRow, handle: ReorderHandleProps | null) =>
    (handle !== null || (row.payee !== null && !readOnly)) && (
      <OverflowMenu label={t('payee.actionsFor', { name: row.name })}>
        {(close) => (
          <>
            {handle !== null && (
              <MenuItem
                disabled={!handle.canMoveEarlier}
                onClick={() => {
                  handle.onMoveEarlier();
                  close();
                }}
              >
                {t('reorder.moveEarlier')}
              </MenuItem>
            )}
            {handle !== null && (
              <MenuItem
                disabled={!handle.canMoveLater}
                onClick={() => {
                  handle.onMoveLater();
                  close();
                }}
              >
                {t('reorder.moveLater')}
              </MenuItem>
            )}
            {row.payee !== null && !readOnly && (
              <MenuItem
                onClick={() => {
                  onToggleExcluded(row.payee!);
                  close();
                }}
              >
                {row.payee.aiExcluded ? t('payee.aiAllowAction') : t('payee.excludeAction')}
              </MenuItem>
            )}
          </>
        )}
      </OverflowMenu>
    );

  const patternButton = (row: DirectoryRow) =>
    row.order !== null &&
    row.order.aliases.length > 0 && (
      <Button
        size="small"
        variant="text"
        onClick={(event) => {
          event.stopPropagation();
          onOpenPatterns(row.id, event.currentTarget);
        }}
      >
        {t('payee.patternCount', { count: row.order.aliases.length })}
      </Button>
    );

  return (
    <Stack spacing={1.5}>
      {orderError !== null && (
        <Alert severity="error" data-order-error="">
          {errorCopy(t, orderError, 'payee.orderUnavailable')}
        </Alert>
      )}
      {reresolve !== null && (
        <Alert
          severity="info"
          data-reresolve=""
          action={
            <Stack direction="row" spacing={1}>
              <Button size="small" onClick={onReresolve} disabled={reresolving}>
                {t('matchingOrder.reresolveNow')}
              </Button>
              <Button size="small" onClick={onDismissReresolve} disabled={reresolving}>
                {t('matchingOrder.notNow')}
              </Button>
            </Stack>
          }
        >
          {t('matchingOrder.wouldChange', { count: reresolve })}
        </Alert>
      )}
      {md ? (
        <TableCard
          title={t('payee.listTitle')}
          name={t('nav.payees')}
          subtitle={t('payee.orderHelp')}
        >
          <SortableTable
            items={rows}
            getId={(row) => row.id}
            getName={(row) => row.name}
            isSortable={(row) => row.order !== null}
            canReorder={showHandles}
            onReorder={onReorder}
            label={t('nav.payees')}
            onRowClick={(row) => onOpen(row.id)}
            head={
              <TableRow>
                {showHandles && (
                  <TableCell sx={{ width: 56, px: 0.5 }}>
                    <Box component="span" sx={visuallyHidden}>
                      {t('reorder.column')}
                    </Box>
                  </TableCell>
                )}
                <TableCell>{t('payee.name')}</TableCell>
                <TableCell>{t('payee.colKind')}</TableCell>
                <TableCell align="right">{t('payee.colTransactions')}</TableCell>
                <TableCell align="right">{t('payee.colTotal')}</TableCell>
                <TableCell>{t('payee.colFirst')}</TableCell>
                <TableCell>{t('payee.colLast')}</TableCell>
                <ActionsHeaderCell />
              </TableRow>
            }
            renderCells={(row, handle) => (
              <>
                {showHandles && <OrderHandleCell handle={handle} />}
                <TableCell>
                  <Stack direction="row" sx={{ gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                    <span>{row.name}</span>
                    {row.payee?.aiExcluded && (
                      <Chip size="small" color="warning" label={t('payee.aiExcludedChip')} />
                    )}
                    {patternButton(row)}
                  </Stack>
                </TableCell>
                <TableCell>
                  {row.payee !== null && (
                    <Chip size="small" label={t(`payee.kind.${row.payee.kind}`)} />
                  )}
                </TableCell>
                <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                  {row.payee?.totals?.transactionCount ?? '—'}
                </TableCell>
                <TableCell align="right">
                  <MoneyText
                    minor={row.payee?.totals?.transactedMinor ?? null}
                    currency={currency}
                  />
                </TableCell>
                <TableCell>
                  {row.payee?.totals?.firstOn
                    ? formatCivilDate(row.payee.totals.firstOn, locale)
                    : '—'}
                </TableCell>
                <TableCell>
                  {row.payee?.totals?.lastOn
                    ? formatCivilDate(row.payee.totals.lastOn, locale)
                    : '—'}
                </TableCell>
                <TableCell onClick={(event) => event.stopPropagation()}>
                  {menu(row, handle)}
                </TableCell>
              </>
            )}
          />
        </TableCard>
      ) : (
        <Stack spacing={1}>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {t('payee.orderHelp')}
          </Typography>
          <SortableTable
            layout="stack"
            items={rows}
            getId={(row) => row.id}
            getName={(row) => row.name}
            isSortable={(row) => row.order !== null}
            canReorder={showHandles}
            onReorder={onReorder}
            label={t('nav.payees')}
            head={null}
            onRowClick={(row) => onOpen(row.id)}
            renderCells={(row, handle) => (
              <Stack direction="row" sx={{ gap: 1, alignItems: 'flex-start' }}>
                {handle !== null && (
                  <Box onClick={(event) => event.stopPropagation()} sx={{ flexShrink: 0 }}>
                    <ReorderHandle {...handle} />
                  </Box>
                )}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Stack direction="row" sx={{ justifyContent: 'space-between', gap: 1 }}>
                    <Typography>{row.name}</Typography>
                    {row.payee !== null && (
                      <Chip size="small" label={t(`payee.kind.${row.payee.kind}`)} />
                    )}
                  </Stack>
                  {row.payee?.aiExcluded && (
                    <Chip
                      size="small"
                      color="warning"
                      label={t('payee.aiExcludedChip')}
                      sx={{ mt: 0.5 }}
                    />
                  )}
                  {patternButton(row)}
                  {row.payee?.totals && (
                    <TabularText component="div" sx={{ mt: 0.5 }}>
                      <MoneyText minor={row.payee.totals.transactedMinor} currency={currency} />
                      {' · '}
                      {row.payee.totals.transactionCount}
                    </TabularText>
                  )}
                </Box>
                <Box onClick={(event) => event.stopPropagation()}>{menu(row, handle)}</Box>
              </Stack>
            )}
          />
        </Stack>
      )}
    </Stack>
  );
}

function PatternPopover({
  menu,
  order,
  onClose,
}: {
  menu: { id: number; anchor: HTMLElement } | null;
  order: PayeeOrderRow[] | null;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const row = order?.find((entry) => entry.payeeId === menu?.id) ?? null;
  return (
    <Popover
      open={menu !== null && row !== null}
      anchorEl={menu?.anchor ?? null}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
    >
      {row !== null && (
        <List dense aria-label={t('payee.patternCount', { count: row.aliases.length })}>
          {row.aliases.map((alias) => (
            <ListItem key={alias.aliasId}>
              <Stack direction="row" sx={{ gap: 1, alignItems: 'baseline', flexWrap: 'wrap' }}>
                <span>{t(ALIAS_KIND_KEYS[kindOf(alias.matchType)])}</span>
                <Typography variant="mono" sx={{ overflowWrap: 'anywhere' }}>
                  {alias.pattern}
                </Typography>
                <span>{t('payee.aliasMatches', { count: alias.matchCount })}</span>
              </Stack>
            </ListItem>
          ))}
        </List>
      )}
    </Popover>
  );
}
